import { useNavigate } from "@tanstack/react-router";
import type { FC } from "react";

import type { EditableTaskStatus, Task } from "@recobra/schemas/task";

import type { TaskProgressInput } from "../task-data";
import { useTaskRequirementContext } from "../task-details";
import type { TaskCompletionInput } from "../task-execution";
import { resolveLockedSolution } from "../task-policy";
import {
  resolveTaskCompletionBlocker,
  type TaskExecutionState,
  taskContextRoute,
} from "../task-policy";
import { TaskModalBody } from "./TaskModalBody";
import { TaskModalHeader } from "./TaskModalHeader";

export const TaskModalContent: FC<{
  task: Task;
  executionState: TaskExecutionState;
  executionOnly: boolean;
  contextRoute: ReturnType<typeof taskContextRoute>;
  isUpdating: boolean;
  onProgress: (input: TaskProgressInput) => void;
  onStatusChange: (status: EditableTaskStatus, waitingStatus?: string) => void;
  onClose: () => void;
  onConfirm: (input: TaskCompletionInput) => void;
}> = ({ task, executionState, executionOnly, contextRoute, isUpdating, ...callbacks }) => {
  const navigate = useNavigate();
  const canExecute = executionState === "allowed";
  const context = useTaskRequirementContext(task);
  const lockedSolution = resolveLockedSolution(task);
  const policy = lockedSolution
    ? { mode: "locked" as const, solution: lockedSolution }
    : { mode: "choose" as const };
  const onViewContext = contextRoute
    ? () => {
        callbacks.onClose();
        void navigate(contextRoute);
      }
    : undefined;

  return (
    <>
      <TaskModalHeader
        task={task}
        isUpdating={isUpdating}
        onViewContext={onViewContext}
        onStatusChange={canExecute ? callbacks.onStatusChange : undefined}
      />
      <TaskModalBody
        task={task}
        canExecute={canExecute}
        completionBlocker={resolveTaskCompletionBlocker(task, executionState)}
        executionOnly={executionOnly}
        context={context}
        policy={policy}
        isUpdating={isUpdating}
        onProgress={callbacks.onProgress}
        onClose={callbacks.onClose}
        onConfirm={callbacks.onConfirm}
      />
    </>
  );
};
